/**
 * Statistical Analysis Framework
 *
 * This framework transforms raw educational KPI data into actionable insights
 * for school principals and education administrators.
 *
 * Architecture: 3-Layer Model
 * ┌─────────────────────────────────────────────────────┐
 * │ Layer 1: DATA LAYER                                 │
 * │ - Raw KPI values                                    │
 * │ - Historical trends                                 │
 * │ - Benchmarks (national, regional)                   │
 * └─────────────────────────────────────────────────────┘
 *                       ↓
 * ┌─────────────────────────────────────────────────────┐
 * │ Layer 2: SIGNAL LAYER                               │
 * │ - Pattern detection                                 │
 * │ - Threshold-based classification                    │
 * │ - Statistical significance                          │
 * └─────────────────────────────────────────────────────┘
 *                       ↓
 * ┌─────────────────────────────────────────────────────┐
 * │ Layer 3: NARRATIVE LAYER (LLM Input)                │
 * │ - Structured insights                               │
 * │ - Prioritized findings                              │
 * │ - Contextual metadata                               │
 * └─────────────────────────────────────────────────────┘
 */

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/**
 * Core analytical dimensions (the "analysis space")
 */
export enum AnalyticalDimension {
  LEVEL = 'level',           // Current performance status
  TREND = 'trend',           // Direction and momentum over time
  BENCHMARK = 'benchmark',   // Comparison to reference groups
  VARIATION = 'variation',   // Equity and consistency
  DRIVERS = 'drivers'        // Explanatory factors
}

/**
 * Performance bands (discretized for clarity)
 */
export enum PerformanceBand {
  WELL_ABOVE = 'well_above',      // > +1 SD above benchmark
  ABOVE = 'above',                 // +0.5 to +1 SD
  AT_LEVEL = 'at_level',          // ±0.5 SD
  BELOW = 'below',                 // -0.5 to -1 SD
  WELL_BELOW = 'well_below'       // < -1 SD below benchmark
}

/**
 * Trend classification (removes noise)
 */
export enum TrendClass {
  STRONG_IMPROVEMENT = 'strong_improvement',     // +3% or more
  IMPROVEMENT = 'improvement',                   // +1% to +3%
  STABLE = 'stable',                             // ±1%
  DECLINE = 'decline',                           // -1% to -3%
  STRONG_DECLINE = 'strong_decline'              // -3% or more
}

/**
 * Signal types (what patterns matter for decisions)
 */
export enum SignalType {
  STRENGTH = 'strength',                 // Green: Above benchmark + positive/stable trend
  LATENT_RISK = 'latent_risk',          // Yellow: Above benchmark but declining
  STRUCTURAL_PROBLEM = 'structural_problem',  // Red: Below benchmark + consistent
  EQUITY_RISK = 'equity_risk',          // Orange: High variation or group gaps
  OPPORTUNITY = 'opportunity'            // Blue: Improving from below
}

/**
 * Decision focus areas (what principals need to act on)
 */
export interface FocusArea {
  area: string;
  priority: 'high' | 'medium' | 'low';
  signal: SignalType;
  affectedKpis: string[];
  suggestedAction: string;
}

// ============================================================================
// DATA LAYER: AGGREGATION SPECIFICATIONS
// ============================================================================

/**
 * Defines what data to extract at each level
 */
export interface DataAggregationSpec {
  // Level (Status)
  level: {
    currentValue: number;
    unit: string;
    normalizedScore?: number;  // Percentile or Z-score
    performanceBand: PerformanceBand;
  };

  // Trend (Movement)
  trend: {
    yearOverYearChange: number;
    yearOverYearChangePct: number;
    trendClass: TrendClass;
    consistentYears: number;  // How many years has trend been consistent?
    historicalValues: Array<{ year: number; value: number }>;
  };

  // Benchmark (Comparison)
  benchmark: {
    reference: 'national' | 'regional' | 'similar_municipalities';
    referenceValue: number;
    differenceAbsolute: number;
    differencePercent: number;
    rank?: number;          // Position among all municipalities
    percentile?: number;
  };

  // Variation (Equity)
  variation: {
    schoolRange: { min: number; max: number; spread: number };
    genderGap?: { boys: number; girls: number; difference: number };
    groupGaps?: Array<{ group: string; value: number; vsAverage: number }>;
    variationLevel: 'low' | 'moderate' | 'high';
  };

  // Drivers (Explanatory)
  drivers?: Array<{
    factor: string;
    associationStrength: 'strong' | 'moderate' | 'weak';
    direction: 'positive' | 'negative';
    confidence: 'high' | 'medium' | 'low';
  }>;
}

// ============================================================================
// SIGNAL LAYER: DETECTION RULES
// ============================================================================

/**
 * Signal detection rules (the "analytical contract")
 *
 * These rules are deterministic and testable.
 */
export class SignalDetector {

  /**
   * Detect STRENGTH signal
   * Criteria: Above benchmark AND (stable or improving)
   */
  static detectStrength(data: DataAggregationSpec): boolean {
    const aboveBenchmark = data.benchmark.differencePercent > 2; // More than 2% above
    const goodTrend = [TrendClass.STABLE, TrendClass.IMPROVEMENT, TrendClass.STRONG_IMPROVEMENT]
      .includes(data.trend.trendClass);

    return aboveBenchmark && goodTrend;
  }

  /**
   * Detect LATENT RISK signal
   * Criteria: Currently good BUT declining
   */
  static detectLatentRisk(data: DataAggregationSpec): boolean {
    const aboveBenchmark = data.benchmark.differencePercent > 0;
    const declining = [TrendClass.DECLINE, TrendClass.STRONG_DECLINE]
      .includes(data.trend.trendClass);
    const consistentDecline = data.trend.consistentYears >= 2;

    return aboveBenchmark && declining && consistentDecline;
  }

  /**
   * Detect STRUCTURAL PROBLEM signal
   * Criteria: Below benchmark AND consistent (not improving)
   */
  static detectStructuralProblem(data: DataAggregationSpec): boolean {
    const belowBenchmark = data.benchmark.differencePercent < -2;
    const notImproving = ![TrendClass.IMPROVEMENT, TrendClass.STRONG_IMPROVEMENT]
      .includes(data.trend.trendClass);

    return belowBenchmark && notImproving;
  }

  /**
   * Detect EQUITY RISK signal
   * Criteria: High variation OR significant group gaps
   */
  static detectEquityRisk(data: DataAggregationSpec): boolean {
    const highVariation = data.variation.variationLevel === 'high';
    const significantGenderGap = data.variation.genderGap &&
      Math.abs(data.variation.genderGap.difference) > 5; // 5 point gender gap

    return highVariation || !!significantGenderGap;
  }

  /**
   * Detect OPPORTUNITY signal
   * Criteria: Below benchmark BUT improving strongly
   */
  static detectOpportunity(data: DataAggregationSpec): boolean {
    const belowBenchmark = data.benchmark.differencePercent < 0;
    const strongImprovement = data.trend.trendClass === TrendClass.STRONG_IMPROVEMENT;

    return belowBenchmark && strongImprovement;
  }

  /**
   * Classify KPI based on all signals
   */
  static classifyKPI(data: DataAggregationSpec): SignalType[] {
    const signals: SignalType[] = [];

    if (this.detectStrength(data)) signals.push(SignalType.STRENGTH);
    if (this.detectLatentRisk(data)) signals.push(SignalType.LATENT_RISK);
    if (this.detectStructuralProblem(data)) signals.push(SignalType.STRUCTURAL_PROBLEM);
    if (this.detectEquityRisk(data)) signals.push(SignalType.EQUITY_RISK);
    if (this.detectOpportunity(data)) signals.push(SignalType.OPPORTUNITY);

    return signals;
  }
}

// ============================================================================
// NARRATIVE LAYER: LLM INPUT SCHEMA
// ============================================================================

/**
 * Structured output for LLM consumption
 *
 * This is what gets sent to the LLM for narrative generation.
 * The LLM should ONLY interpret and prioritize, NOT calculate.
 */
export interface StatisticalAnalysisInput {
  // Meta information
  meta: {
    municipalityId: string;
    municipalityName: string;
    analysisDate: string;
    academicYear: number;
    comparisonBenchmark: 'national' | 'regional';
  };

  // Overall assessment (synthesized)
  overallAssessment: {
    status: PerformanceBand;
    trend: TrendClass;
    systemRisk: 'low' | 'moderate' | 'high';
    equityLevel: 'high' | 'moderate' | 'low';
  };

  // Key findings (prioritized by signal strength)
  keyFindings: {
    strengths: Array<{
      kpiId: string;
      kpiName: string;
      signal: SignalType;
      summary: string;
    }>;
    risks: Array<{
      kpiId: string;
      kpiName: string;
      signal: SignalType;
      severity: 'high' | 'medium' | 'low';
      summary: string;
    }>;
    opportunities: Array<{
      kpiId: string;
      kpiName: string;
      signal: SignalType;
      potential: 'high' | 'medium' | 'low';
      summary: string;
    }>;
  };

  // Dimensional insights
  insights: {
    performanceHighlights: string[];  // 2-3 bullets
    trendWarnings: string[];          // 1-2 bullets if any
    equityGaps: string[];             // 1-2 bullets if any
    driverInsights: string[];         // 1-2 bullets if any
  };

  // Recommended focus areas (top 2-3)
  focusAreas: FocusArea[];
}

/**
 * LLM prompt template
 *
 * This defines what the LLM should do with the structured data.
 */
export const LLM_PROMPT_TEMPLATE = `
Du är en erfaren utbildningsanalytiker som sammanfattar skolstatistik för beslutsfattare.

Baserat på nedanstående strukturerade data, skriv en extremt kortfattad analys (max 120 ord) i tre tydliga delar:

1. **Rubrik/Takeaway**: En enda kraftfull mening som sammanfattar läget (t.ex. "**Stabil trend men svagt resultat jämfört med riksgenomsnittet.**"). Gör gärna meningen fetstilt.

VIKTIGT: Lägg två tomma rader efter rubriken.

2. **Observationer**: 2-3 korta punkter med de viktigaste sakliga fynden. Använd punktlistor (* eller -).
3. **Åtgärd**: En mening om var fokus bör ligga för framgångsrikt förändringsarbete.

**Viktiga principer:**
- Använd ett professionellt men mänskligt språk (inte akademiskt).
- Undvik att bara rabbla siffror – tolka vad siffrorna BETYDER för verksamheten.
- Var konkret och handlingsorienterad.
- Ta inte med metodologiska förklaringer.

**Strukturerad data:**
{JSON-INPUT}

Skriv analysen här:
`;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Classify trend based on year-over-year change
 */
export function classifyTrend(yoyChangePct: number): TrendClass {
  if (yoyChangePct >= 3) return TrendClass.STRONG_IMPROVEMENT;
  if (yoyChangePct >= 1) return TrendClass.IMPROVEMENT;
  if (yoyChangePct > -1) return TrendClass.STABLE;
  if (yoyChangePct > -3) return TrendClass.DECLINE;
  return TrendClass.STRONG_DECLINE;
}

/**
 * Classify performance band based on Z-score or percentile
 */
export function classifyPerformanceBand(
  value: number,
  benchmark: number,
  stdDev: number
): PerformanceBand {
  const zScore = (value - benchmark) / stdDev;

  if (zScore > 1) return PerformanceBand.WELL_ABOVE;
  if (zScore > 0.5) return PerformanceBand.ABOVE;
  if (zScore > -0.5) return PerformanceBand.AT_LEVEL;
  if (zScore > -1) return PerformanceBand.BELOW;
  return PerformanceBand.WELL_BELOW;
}

/**
 * Classify variation level based on coefficient of variation
 */
export function classifyVariation(range: { min: number; max: number }, mean: number): 'low' | 'moderate' | 'high' {
  const spread = range.max - range.min;
  const cv = (spread / 2) / mean; // Approximate CV

  if (cv < 0.1) return 'low';
  if (cv < 0.2) return 'moderate';
  return 'high';
}

/**
 * Calculate consistency of trend (how many consecutive years in same direction)
 */
export function calculateTrendConsistency(historicalValues: Array<{ year: number; value: number }>): number {
  if (historicalValues.length < 2) return 0;

  let consistent = 1;
  const sorted = [...historicalValues].sort((a, b) => b.year - a.year);

  const latestTrend = sorted[0].value - sorted[1].value;
  const isIncreasing = latestTrend > 0;

  for (let i = 1; i < sorted.length - 1; i++) {
    const change = sorted[i].value - sorted[i + 1].value;
    if ((isIncreasing && change > 0) || (!isIncreasing && change < 0)) {
      consistent++;
    } else {
      break;
    }
  }

  return consistent;
}

// ============================================================================
// EXPORTS
// ============================================================================
// Note: All exports are already done inline above with 'export' keyword
